import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Entity, Alert, GraphNode } from '../types';

interface UserState {
  id: string | null;
  name: string;
  email: string;
  role: string;
  avatar: string | null;
}

interface SidebarState {
  collapsed: boolean;
  activeSection: string;
}

interface NotificationState {
  unreadCount: number;
  notifications: Array<{
    id: string;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success';
    read: boolean;
    timestamp: string;
  }>;
}

interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  authLogin: (token: string, user: { id: string; name: string; email: string; role: string; avatar: string | null }) => void;
  /**
   * Remplace le JETON seul, sans toucher a l'identite.
   *
   * ⚠️ `useRefreshToken` appelait `authLogin` en lui re-passant le `user` lu
   * dans le store. Quand ce `user` etait l'etat initial, le rafraichissement
   * CIMENTAIT le role par defaut au lieu de le laisser se resoudre.
   */
  authSetToken: (token: string) => void;
  authLogout: () => void;
}

interface AppState extends AuthState {
  // User
  user: UserState;
  setUser: (user: Partial<UserState>) => void;
  clearUser: () => void;

  // Theme
  theme: 'dark' | 'light';
  toggleTheme: () => void;

  // Sidebar
  sidebar: SidebarState;
  toggleSidebar: () => void;
  setActiveSection: (section: string) => void;

  // Active entity context
  selectedEntity: Entity | null;
  setSelectedEntity: (entity: Entity | null) => void;
  selectedNodes: GraphNode[];
  setSelectedNodes: (nodes: GraphNode[]) => void;
  addSelectedNode: (node: GraphNode) => void;
  removeSelectedNode: (nodeId: string) => void;

  // Notifications
  notifications: NotificationState;
  addNotification: (notification: Omit<NotificationState['notifications'][0], 'id' | 'read' | 'timestamp'>) => void;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  clearNotifications: () => void;

  // Global search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: Entity[];
  setSearchResults: (results: Entity[]) => void;

  // Alert context
  activeAlerts: Alert[];
  setActiveAlerts: (alerts: Alert[]) => void;

  // Graph view state
  graphViewMode: '2d' | '3d';
  setGraphViewMode: (mode: '2d' | '3d') => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Auth
      token: null,
      isAuthenticated: false,
      authLogin: (token, user) =>
        set({
          token,
          isAuthenticated: true,
          user: {
            id: user.id,
            name: user.name,
            email: user.email,
            role: user.role,
            avatar: user.avatar,
          },
        }),
      authSetToken: (token) => set({ token, isAuthenticated: true }),
      authLogout: () =>
        set({
          token: null,
          isAuthenticated: false,
          user: { id: null, name: '', email: '', role: '', avatar: null },
        }),

      // User
      // ⚠️ D.1308 — AUCUN ROLE PAR DEFAUT. `role: 'analyst'` etait ici, et
      // `partialize` (plus bas) ne persiste ni `user` ni `isAuthenticated` :
      // au rechargement, un admin authentifie repassait par cette valeur et
      // `ProtectedRoute` lui affichait « Acces refuse — Votre role : analyst ».
      // Un role vide veut dire « pas encore su », et c'est ce que les lecteurs
      // doivent distinguer d'un refus. Meme regle que le correctif serveur
      // D.1320 : en l'absence d'information, on ne devine pas.
      // ⚠️ Mesure avant de retirer : `user.role` du store n'est lu QUE par
      // `ProtectedRoute.tsx`, et `user.name` par aucun rendu. Retirer ces
      // valeurs ne masque donc aucune fonctionnalite.
      user: {
        id: null,
        name: '',
        email: '',
        role: '',
        avatar: null,
      },
      setUser: (user) =>
        set((state) => ({ user: { ...state.user, ...user } })),
      clearUser: () =>
        set({
          user: { id: null, name: '', email: '', role: '', avatar: null },
        }),

      // Theme
      theme: 'dark',
      toggleTheme: () =>
        set((state) => ({
          theme: state.theme === 'dark' ? 'light' : 'dark',
        })),

      // Sidebar
      sidebar: { collapsed: false, activeSection: 'dashboard' },
      toggleSidebar: () =>
        set((state) => ({
          sidebar: { ...state.sidebar, collapsed: !state.sidebar.collapsed },
        })),
      setActiveSection: (section) =>
        set((state) => ({
          sidebar: { ...state.sidebar, activeSection: section },
        })),

      // Entity selection
      selectedEntity: null,
      setSelectedEntity: (entity) => set({ selectedEntity: entity }),
      selectedNodes: [],
      setSelectedNodes: (nodes) => set({ selectedNodes: nodes }),
      addSelectedNode: (node) =>
        set((state) => ({
          selectedNodes: [...state.selectedNodes, node],
        })),
      removeSelectedNode: (nodeId) =>
        set((state) => ({
          selectedNodes: state.selectedNodes.filter((n) => n.id !== nodeId),
        })),

      // Notifications
      notifications: { unreadCount: 0, notifications: [] },
      addNotification: (notification) =>
        set((state) => {
          const newNotif = {
            ...notification,
            id: crypto.randomUUID(),
            read: false,
            timestamp: new Date().toISOString(),
          };
          return {
            notifications: {
              unreadCount: state.notifications.unreadCount + 1,
              notifications: [newNotif, ...state.notifications.notifications].slice(0, 100),
            },
          };
        }),
      markNotificationRead: (id) =>
        set((state) => ({
          notifications: {
            unreadCount: Math.max(0, state.notifications.unreadCount - 1),
            notifications: state.notifications.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
          },
        })),
      markAllNotificationsRead: () =>
        set((state) => ({
          notifications: {
            unreadCount: 0,
            notifications: state.notifications.notifications.map((n) => ({
              ...n,
              read: true,
            })),
          },
        })),
      clearNotifications: () =>
        set({ notifications: { unreadCount: 0, notifications: [] } }),

      // Global search
      searchQuery: '',
      setSearchQuery: (query) => set({ searchQuery: query }),
      searchResults: [],
      setSearchResults: (results) => set({ searchResults: results }),

      // Alerts
      activeAlerts: [],
      setActiveAlerts: (alerts) => set({ activeAlerts: alerts }),

      // Graph
      graphViewMode: '2d',
      setGraphViewMode: (mode) => set({ graphViewMode: mode }),
    }),
    {
      name: 'helios-store',
      partialize: (state) => ({
        theme: state.theme,
        sidebar: state.sidebar,
        graphViewMode: state.graphViewMode,
      }),
    }
  )
);
